import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { degreeToRadiansUseCase } from './application/use-cases/degrees-to-radians.use-case';

import { MarketConfigurable } from '@core/domain/impl/market-configurable.impl';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { getCountryCode } from '@core/infrastructure/state/core.selectors';
import { ItxObservabilityCommands } from '@frontend/libs/itx-observability-core';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { StaticResourceService } from '@shared/services/static-resources/static-resource.service';

/**
 * Servicio para la gestión de mapas
 */
@Injectable()

/**
 * Map service
 */
export class MapService extends MarketConfigurable<Record<string, any>> {
    // private readonly defaulCoords = { lat: -34.397, lng: 150.644 };
    // private readonly MAPS_ZOOM_ICONS_CHANGE: number = 9;
    // private readonly STORELOCATOR_MAX_STORE_COUNT: number = 0;
    // private locations: any;
    public BAIDU_TYPE = 'baidu';

    public GOOGLE_TYPE = 'google';

    protected readonly config: Record<string, Record<string, any>> = {
        CN: {
            mapProvider: this.BAIDU_TYPE,
        },
        default: {
            mapProvider: this.GOOGLE_TYPE,
        },
    };

    private readonly platformId = inject(PLATFORM_ID);

    private readonly staticResourceSvc: StaticResourceService = inject(StaticResourceService);

    private readonly store: Store = inject(Store);

    private readonly window: Partial<CommonWindow> = inject(WINDOW);

    private readonly countryCode: string = this.store.selectSignal(getCountryCode)();

    public mapProvider: string = this.getConfigByMarketCode(this.countryCode).mapProvider;

    // DEPRECATED: mapProvider$ is being deprecated. Please use the synchronous alternative instead
    public mapProvider$: Observable<string> = this.store.select(getCountryCode).pipe(
        map((countryCode) => this.getConfigByMarketCode(countryCode).mapProvider)
    );

    public readonly DISTANCE_TO_BOUNDS: number = 30;

    public selectedStore: any = false;

    public params: any;

    public kindMap = 'google';

    public page: 'pickup' | 'store-locator' = 'store-locator';

    public providersWithoutIcon: string[] = [];

    public providersLoaded: Record<string, string> = {};

    public readonly markerIcons: any = {
        unused: {
            icon: {
                url: this.staticResourceSvc.getImage('map/stores/unselected_store.svg', true),
            },
        },
        used: {
            icon: {
                url: this.staticResourceSvc.getImage('map/stores/selected_store.svg', true),
            },
        },
        disabled: {
            icon: {
                url: this.staticResourceSvc.getImage('map/stores/disabled_store.svg', true),
            },
        },
        center: {
            icon: {
                url: this.staticResourceSvc.getImage('map/stores/pin-point.svg', true),
            },
        },
        summary: {
            icon: {
                url: this.staticResourceSvc.getImage('map/stores/logo.png', true),
            },
        },
    };

    public isInMobileSize(): boolean {
        return isPlatformBrowser(this.platformId)
            ? [Resolutions.MOBILE, Resolutions.TABLET].includes(this.store.selectSignal(getResolution)())
            : false;
    }

    /**
     * @param type store or droppoint
     * @param mode different icon modes used, unused, summary ...
     * @param countryCode code for droppoint providers
     * @param provider droppoint provider
     */
    public getMarkerIcon(type: string, mode: string, countryCode?: string, provider?: string): any {
        let path = null;

        if (type === 'store') {
            path = this.markerIcons[mode].icon.url;
            return path;
        }

        path = `map/droppoints/${countryCode.toLowerCase()}/${mode}/${provider}.png`;
        return this.staticResourceSvc.getImage(path, true);
    }

    public getBaseIconUrl(mode: string, provider: string): string {
        if (provider == null) {
            return this.getMarkerIcon('store', mode);
        }

        if (this.providersLoaded[provider]) {
            return this.providersLoaded[provider];
        }

        let url = this.getMarkerIcon('droppoint', mode, this.countryCode, provider);

        this.providersLoaded[provider] = url;
        if (this.providersWithoutIcon?.includes(provider)) {
            return this.staticResourceSvc.getImage('map/droppoints/generic.png', true);
        }
        const hasError = this.getBaseIconUrlAsync(url);

        if (hasError) {
            this.providersWithoutIcon.push(provider);
            url = this.staticResourceSvc.getImage('map/droppoints/generic.png', true);
        }

        return url;
    }

    private getBaseIconUrlAsync(url: string): boolean {
        let hasError = false;
        fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Error fetching URL: ${url}`);
                }
                return response.json();
            })
            .catch(err => {
                ItxObservabilityCommands.sendObservabilityLog(err, 'getBaseIconUrl');
                hasError = true;
            });
        return !hasError; // Esta función es sincrónica, pero el comportamiento es asíncrono.
    }

    public getMarkerIconUrl(mode: string, markerIcon: any): string {
        const marketType: string = markerIcon.kind ? 'droppoint' : 'store';

        if (marketType === 'droppoint') {
            if (this.providersWithoutIcon?.includes(markerIcon.kind)) {
                return this.staticResourceSvc.getImage('map/droppoints/generic.png', true);
            }
            return this.getMarkerIcon('droppoint', mode, this.countryCode, markerIcon.kind);
        }
        return this.getMarkerIcon('store', mode);
    }

    // Refactor to use-case
    public computeDistance(p1lat, p1long, p2lat, p2long): number {
        const radius = 6731;
        const dLat = degreeToRadiansUseCase(p2lat - p1lat);
        const dLon = degreeToRadiansUseCase(p2long - p1long);
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
            + Math.cos(degreeToRadiansUseCase(p1lat))
            * Math.cos(degreeToRadiansUseCase(p2lat))
            * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = radius * c; // Distance in m
        return d;
    }

    // Refactor to use-case
    public calcDistanceBetween(p1, p2): number {
        const R = 6378137; // Earth’s mean radius in meter
        const dLat = degreeToRadiansUseCase(p2.latitude - p1.latitude);
        const dLong = degreeToRadiansUseCase(p2.longitude - p1.longitude);
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
            + Math.cos(degreeToRadiansUseCase(
                p1.latitude
            )) * Math.cos(degreeToRadiansUseCase(p2.latitude)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c;
        return d; // returns the distance in meters
    }

    public getClusterInlineSvg(color: string): string {
        const encoded = this.window.btoa(`<svg   xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                                  <circle cx="26" cy="26" r="26" fill="${color}"/>
                                </svg>`);

        return `data:image/svg+xml;base64,${encoded}`;
    }
}
